// Microfrasi e saluti per presenza
const microPhrases = [
  {
    it: "Un piccolo gesto. Oggi basta questo.",
    en: "A small gesture. That's enough for today.",
    es: "Un pequeño gesto. Hoy basta con esto."
  },
  {
    it: "Anche un dettaglio conta.",
    en: "Even a detail matters.",
    es: "Incluso un detalle cuenta."
  },
  {
    it: "La costanza fa la differenza.",
    en: "Consistency makes the difference.",
    es: "La constancia marca la diferencia."
  },
  {
    it: "Un passo alla volta.",
    en: "One step at a time.",
    es: "Un paso a la vez."
  },
  {
    it: "Oggi, basta anche solo questo.",
    en: "Today, this is enough.",
    es: "Hoy, basta con esto."
  }
];

import React, { useState, useEffect, useCallback, useRef } from 'react';
import "@fontsource/inter";
import "@fontsource/dm-sans";

import { App as CapacitorApp } from '@capacitor/app';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { toast } from 'sonner';
import CheckInModal from '../components/CheckInModal';
import AlternativeModal from '../components/AlternativeModal';
import AlternativePaywall from '../components/AlternativePaywall';
import PremiumPaywall from '../components/PremiumPaywall';
import ActionDetailPanel from '../components/ActionDetailPanel';
import ActionCard from '../components/ActionCard';
import ProgressivePromptCard from '../components/ProgressivePromptCard';
import ProgressiveModal from '../components/ProgressiveModal';
import { createInitialGarden, stepGrowth } from '../components/experimental/isometricGardenModel';
import Loading from '../components/Loading';
import DashboardSkeleton from '../components/skeletons/DashboardSkeleton';
import AchievementCelebrationModal from '../components/AchievementCelebrationModal';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useUser } from '@/contexts/UserContext';
import { trackSuggestionShown, trackSuggestionAction, trackPaywallViewed } from '@/lib/analytics';
import { useStats } from '@/contexts/StatsContext';
import { useHeader } from '@/contexts/HeaderContext';
import Inter from '@fontsource/inter';
import DMSans from '@fontsource/dm-sans';

/* ═══ Hold-to-Complete Button ═══ */
const HOLD_DURATION = 2000;
const HOLD_COLORS = ['#2a4a2a','#3d6b3d','#7EA86C','#EF9F27','#ED93B1'];

const BEEP_SCHEDULE = [
  { p: 0.20, freq: 280, vol: 0.07, dur: 0.08 },
  { p: 0.35, freq: 320, vol: 0.09, dur: 0.08 },
  { p: 0.50, freq: 380, vol: 0.11, dur: 0.09 },
  { p: 0.63, freq: 440, vol: 0.13, dur: 0.09 },
  { p: 0.74, freq: 520, vol: 0.15, dur: 0.10 },
  { p: 0.83, freq: 600, vol: 0.17, dur: 0.10 },
  { p: 0.90, freq: 700, vol: 0.19, dur: 0.11 },
  { p: 0.95, freq: 820, vol: 0.21, dur: 0.11 },
  { p: 0.98, freq: 980, vol: 0.23, dur: 0.12 },
];

let holdAudioCtx = null;
function getHoldAudio() {
  if (!holdAudioCtx) holdAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return holdAudioCtx;
}
function playBip(freq, vol, duration) {
  try {
    const ac = getHoldAudio();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    const now = ac.currentTime;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(vol, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain); gain.connect(ac.destination);
    osc.start(now); osc.stop(now + duration + 0.01);
  } catch (_) { /* audio not available */ }
}
function playCompleteSound() {
  try {
    const ac = getHoldAudio();
    const notes = [[523.25, 0.00], [659.25, 0.09], [783.99, 0.18], [1046.5, 0.28]];
    notes.forEach(([freq, when]) => {
      const osc = ac.createOscillator(); const gain = ac.createGain();
      const now = ac.currentTime + when;
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.015, now + 0.12);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.28, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
      osc.connect(gain); gain.connect(ac.destination);
      osc.start(now); osc.stop(now + 0.4);
    });
  } catch (_) { /* audio not available */ }
}

function lerpVal(a, b, t) { return a + (b - a) * t; }
function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
function hexToRgb(hex) { return [parseInt(hex.slice(1,3),16), parseInt(hex.slice(3,5),16), parseInt(hex.slice(5,7),16)]; }
function lerpColor(h1, h2, t) {
  const [r1,g1,b1] = hexToRgb(h1), [r2,g2,b2] = hexToRgb(h2);
  return `rgb(${Math.round(lerpVal(r1,r2,t))},${Math.round(lerpVal(g1,g2,t))},${Math.round(lerpVal(b1,b2,t))})`;
}

class HoldParticle {
  constructor(cx, cy, burst) {
    const angle = Math.random() * Math.PI * 2;
    const speed = burst ? (3 + Math.random() * 5) : (1 + Math.random() * 2);
    this.x = cx; this.y = cy;
    this.vx = Math.cos(angle) * speed; this.vy = Math.sin(angle) * speed;
    this.life = 1;
    this.decay = burst ? (0.016 + Math.random() * 0.018) : (0.03 + Math.random() * 0.02);
    this.r = burst ? (3 + Math.random() * 4) : (2 + Math.random() * 2);
    this.color = HOLD_COLORS[Math.floor(Math.random() * HOLD_COLORS.length)];
    this.gravity = 0.07;
  }
  update() { this.x += this.vx; this.y += this.vy; this.vy += this.gravity; this.vx *= 0.97; this.life -= this.decay; }
  draw(ctx) {
    if (this.life <= 0) return;
    ctx.save(); ctx.globalAlpha = Math.max(0, this.life);
    ctx.fillStyle = this.color; ctx.beginPath();
    ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2); ctx.fill(); ctx.restore();
  }
}

const HoldToCompleteButton = ({ onComplete, label, isCompleting }) => {
  const canvasRef = useRef(null);
  const btnRef = useRef(null);
  const holdingRef = useRef(false);
  const doneRef = useRef(false);
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const particleLoopRef = useRef(false);
  const pulseIntervalRef = useRef(null);
  const bipsPlayedRef = useRef(new Array(BEEP_SCHEDULE.length).fill(false));
  const [holdDone, setHoldDone] = useState(false);
  const [labelText, setLabelText] = useState(label);

  useEffect(() => { setLabelText(label); }, [label]);

  const spawnParticles = useCallback((n, burst) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cx = canvas.width / 2, cy = canvas.height / 2;
    for (let i = 0; i < n; i++) particlesRef.current.push(new HoldParticle(cx, cy, burst));
    if (!particleLoopRef.current) {
      particleLoopRef.current = true;
      const loop = () => {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particlesRef.current = particlesRef.current.filter(p => p.life > 0);
        particlesRef.current.forEach(p => { p.update(); p.draw(ctx); });
        if (particlesRef.current.length > 0) requestAnimationFrame(loop);
        else particleLoopRef.current = false;
      };
      requestAnimationFrame(loop);
    }
  }, []);

  const checkBips = useCallback((p) => {
    BEEP_SCHEDULE.forEach((b, i) => {
      if (!bipsPlayedRef.current[i] && p >= b.p) {
        bipsPlayedRef.current[i] = true;
        playBip(b.freq, b.vol, b.dur);
        // Progressive haptic feedback at each beep point
        try { Haptics.impact({ style: p > 0.8 ? ImpactStyle.Heavy : ImpactStyle.Light }); } catch (_) {}
      }
    });
  }, []);

  const setProgress = useCallback((p) => {
    const btn = btnRef.current;
    if (!btn) return;
    const size = lerpVal(100, 130, easeOutCubic(p));
    btn.style.width = size + 'px';
    btn.style.height = size + 'px';
    btn.style.borderRadius = '999px';
    const startColor = getComputedStyle(document.documentElement).getPropertyValue('--c-btn-done-start').trim() || '#1a2e1a';
    btn.style.background = lerpColor(startColor, '#5C8748', easeOutCubic(p));
    const shake = p > 0.8 ? Math.sin(Date.now() / 40) * 2.5 * ((p - 0.8) / 0.2) : 0;
    btn.style.transform = `translateX(${shake}px)`;
    checkBips(p);
  }, [checkBips]);

  const complete = useCallback(() => {
    doneRef.current = true;
    holdingRef.current = false;
    clearInterval(pulseIntervalRef.current);
    setHoldDone(true);
    const btn = btnRef.current;
    if (btn) {
      btn.style.background = '#4CD964';
      btn.style.width = '100px';
      btn.style.height = '100px';
      btn.style.borderRadius = '999px';
      btn.style.transform = '';
    }
    spawnParticles(45, true);
    playCompleteSound();
    try { Haptics.impact({ style: ImpactStyle.Medium }); } catch (_) {}
    setLabelText('');
    onComplete();
  }, [onComplete, spawnParticles]);

  const startHold = useCallback((e) => {
    if (doneRef.current || isCompleting) return;
    e.preventDefault();
    // Wake up AudioContext on user gesture
    try { getHoldAudio(); } catch (_) {}
    holdingRef.current = true;
    bipsPlayedRef.current = new Array(BEEP_SCHEDULE.length).fill(false);
    startTimeRef.current = performance.now();
    pulseIntervalRef.current = setInterval(() => {
      if (!holdingRef.current) { clearInterval(pulseIntervalRef.current); return; }
      spawnParticles(2, false);
    }, 130);
    const tick = (now) => {
      if (!holdingRef.current || doneRef.current) return;
      const p = Math.min((now - startTimeRef.current) / HOLD_DURATION, 1);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else complete();
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [isCompleting, spawnParticles, setProgress, complete]);

  const stopHold = useCallback(() => {
    if (doneRef.current) return;
    holdingRef.current = false;
    clearInterval(pulseIntervalRef.current);
    cancelAnimationFrame(rafRef.current);
    const btn = btnRef.current;
    if (!btn) return;
    const cur = parseFloat(btn.style.width || 100);
    const from = (cur - 100) / (130 - 100);
    const start = performance.now();
    const snap = (now) => {
      const t = Math.min((now - start) / 350, 1);
      setProgress(from * (1 - easeOutCubic(t)));
      if (t < 1) requestAnimationFrame(snap);
      else { btn.style.transform = ''; }
    };
    requestAnimationFrame(snap);
  }, [setProgress]);

  useEffect(() => {
    const handler = () => stopHold();
    window.addEventListener('mouseup', handler);
    window.addEventListener('touchend', handler);
    return () => {
      window.removeEventListener('mouseup', handler);
      window.removeEventListener('touchend', handler);
      clearInterval(pulseIntervalRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [stopHold]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0 8px' }}>
      <style>{`
        .hold-to-complete { -webkit-appearance: none; overflow: hidden; }
      `}</style>
      <div style={{ position: 'relative', width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <canvas ref={canvasRef} width={180} height={180} style={{ position: 'absolute', top: 0, left: 0, width: 180, height: 180, pointerEvents: 'none' }} />
        <button
          ref={btnRef}
          onMouseDown={startHold}
          onTouchStart={startHold}
          disabled={isCompleting || holdDone}
          className="hold-to-complete"
          style={{
            width: 100, height: 100, borderRadius: 999,
            border: 'none', background: holdDone ? '#5C8748' : 'var(--c-btn-done-start)',
            cursor: isCompleting || holdDone ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', zIndex: 3, touchAction: 'none',
            userSelect: 'none', WebkitUserSelect: 'none',
          }}
        >
          {holdDone ? (
            <span style={{ fontSize: 28, pointerEvents: 'none' }} role="img" aria-label="seed">🌱</span>
          ) : (
            <span style={{ fontFamily: 'Manrope-Variable', fontSize: 15, fontWeight: 700, color: '#fff', pointerEvents: 'none', letterSpacing: '0.02em' }}>
              {label}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  // ...existing hook declarations...

  // --- Animazione Glow per la freccia sopra l'azione del giorno ---
  const [glow, setGlow] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setGlow(true);
      setTimeout(() => setGlow(false), 1000);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const API = `${BACKEND_URL}/api`;
  // ...existing hook declarations...
  // Random microfrase e saluto
  const [microIndex, setMicroIndex] = useState(() => Math.floor(Math.random() * microPhrases.length));
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['translation', 'common']);
    const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinDone, setCheckinDone] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(true);
  const [isEvening, setIsEvening] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState(5);
  const [requestingNew, setRequestingNew] = useState(false);
  const [showActionDetail, setShowActionDetail] = useState(false);
  const [showAlternativeOption, setShowAlternativeOption] = useState(false);
  const [alternativeCount, setAlternativeCount] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const [skipTextIndex, setSkipTextIndex] = useState(0);

  // Skip text rotation
  const skipTexts = [
    t('dashboard.skip.notToday') || "Not today",
    t('dashboard.skip.dontFeelLikeIt') || "I don't feel like it"
  ];
  const [showAlternativeModal, setShowAlternativeModal] = useState(false);
  const [showAlternativePaywall, setShowAlternativePaywall] = useState(false);
  const [progressiveQuestion, setProgressiveQuestion] = useState(null);
  const [showProgressiveModal, setShowProgressiveModal] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [showSkipBanner, setShowSkipBanner] = useState(false);
  const [skipBannerText, setSkipBannerText] = useState('');
  const [showPrecisionPrompt, setShowPrecisionPrompt] = useState(false);
  const [showPrecisionModal, setShowPrecisionModal] = useState(false);
  const [showPremiumPrecisionModal, setShowPremiumPrecisionModal] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionPhrase, setCompletionPhrase] = useState('');
  const [showNoteSheet, setShowNoteSheet] = useState(false);
  const [showGrokModal, setShowGrokModal] = useState(false);
  const [grokQuestion, setGrokQuestion] = useState('');
  const [grokResponse, setGrokResponse] = useState('');
  const [grokLoading, setGrokLoading] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const [needsGenerate, setNeedsGenerate] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [internalizedHidden, setInternalizedHidden] = useState(false);
  const [celebrationAchievement, setCelebrationAchievement] = useState(null);
  const [celebrationQueue, setCelebrationQueue] = useState([]);

  // Garden state — managed in AuthenticatedLayout, accessed via outlet context
  const { gardenGrid, setGardenGrid, setCompletionAnim, setForestPulse } = useOutletContext();
  const [gardenRestored, setGardenRestored] = useState(false);
  const [doneCheckVisible, setDoneCheckVisible] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceActionText, setReplaceActionText] = useState('');

  // KPI: track paywall views
  useEffect(() => {
    if (showPaywall && userId) {
      trackPaywallViewed(userId, { source: 'dashboard' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaywall]);

  const visibilityListenerRef = useRef(null);

  useEffect(() => {
    setMicroIndex(Math.floor(Math.random() * microPhrases.length));
  }, [suggestion?.main_suggestion?.text]);

  // If suggestion already persisted as done, ensure we show a completion phrase
  useEffect(() => {
    if (suggestion?.status === 'done' && !completionPhrase) {
      // debug: verify effect runs in tests
      const phrases = t('dashboard.completion.phrases', { returnObjects: true }) || [];
      const fallback = microPhrases.map(p => (i18n.language === 'it' ? p.it : i18n.language === 'es' ? p.es : p.en));
      const choices = (Array.isArray(phrases) && phrases.length) ? phrases : fallback;
      const random = choices[Math.floor(Math.random() * choices.length)];
      setCompletionPhrase(random);
    }
  }, [suggestion?.status, completionPhrase, t, i18n.language, microIndex]);

  useEffect(() => {
    const timer = setTimeout(() => setButtonVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);



  // Context (NO try/catch: rules of hooks)
  const { user, isPremium, isAdmin, isBetatester, loading: userLoading, token, fetchUserData } = useUser();
  const isFreeDebug = user?.role === "free-debug" || user?.subscription_status === "free-debug";
  const hasPremiumFeatureAccess = isPremium || isAdmin || isBetatester;
  // DEBUG: log free-debug flag
  useEffect(() => {
    console.log('[Dashboard] isFreeDebug', isFreeDebug, 'user', user);
    if (isFreeDebug && window.toast) {
      window.toast('free-debug mode active', { duration: 3000 });
    }
  }, [isFreeDebug, user]);
  const { setHeaderProps } = useHeader();
  const { stats, areaStats, refreshStats } = useStats();

  const userId = user?.user_id || user?.id;
  const canSeeDetailedStats = isPremium || isAdmin || isBetatester || isFreeDebug;
  const redirectToSubscription = useCallback(() => {
    navigate('/subscription');
  }, [navigate]);

  // Restore garden from backend forest_level when localStorage is empty
  useEffect(() => {
    if (!userId || !token || gardenRestored) return;
    setGardenRestored(true);

    // Check if we already have a non-trivial grid (i.e. has plants, not just grass)
    const hasPlants = gardenGrid.flat().some(t => t !== 'grass' && t !== 'empty');
    if (hasPlants) return;

    // No saved grid — rebuild from backend forest_level
    const rebuild = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/forest/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const level = res.data?.forest_level || 0;
        if (level > 0) {
          let g = createInitialGarden(userId);
          // Each forest_level represents 1 stepGrowth call (plants a full tree)
          const steps = level;
          for (let i = 0; i < steps; i++) {
            const result = stepGrowth(g);
            g = result.grid;
            if (result.done) break;
          }
          setGardenGrid(g);
        }
      } catch (err) {
        console.warn('[Dashboard] Could not restore garden from backend:', err);
      }
    };
    rebuild();
  }, [userId, token, gardenRestored, gardenGrid, setGardenGrid]);

  // Load stats on component mount
  useEffect(() => {
    refreshStats();
    // eslint-disable-next-line
  }, [userId]);

  // Fetch progressive question
  useEffect(() => {
    const fetchProgressive = async () => {
      if (!hasPremiumFeatureAccess) {
        setProgressiveQuestion(null);
        return;
      }
      // allow fetching when admin button sets query param even if onboarding not done
      const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const explicit = params?.get('showProgressive');
      if ((user?.onboarding_completed_at || explicit) && !showActionDetail) {
        try {
          const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
          const res = await axios.get(`${API}/profile/progressive/next`, config);
          if (res.data && res.data.key) {
            setProgressiveQuestion(res.data);
          }
        } catch (error) {
          console.error("Error fetching progressive question:", error);
        }
      }
    };
    fetchProgressive();
  }, [userId, user?.onboarding_completed_at, showActionDetail, API, token, hasPremiumFeatureAccess]);

  // if query parameter asks for progressive modal, open it once question is loaded
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('showProgressive') && progressiveQuestion) {
        setShowProgressiveModal(true);
        // remove param so it doesn't reopen on navigation back
        params.delete('showProgressive');
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    } catch (e) {
      // ignore if window not defined or URLAPI fails
    }
  }, [progressiveQuestion]);

  // if query parameter asks for check-in modal (navigated from bell on another page)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('openCheckin')) {
        setShowCheckin(true);
        params.delete('openCheckin');
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Listen for open-checkin event (fired when evening notification tapped while already on dashboard)
  useEffect(() => {
    const handleOpenCheckin = () => setShowCheckin(true);
    window.addEventListener('open-checkin', handleOpenCheckin);
    return () => window.removeEventListener('open-checkin', handleOpenCheckin);
  }, []);

  // ...existing code...

  // Helper function for robust translation with fallbacks
  const getTranslation = (key, fallback) => {
    const translated = t(key);
    if (translated && translated !== key) return translated;
    return fallback;
  };



  /* --------------------------------------------------
     UTILS
  -------------------------------------------------- */

  const getStreakLevel = (streak) => {
    if (streak >= 30) return { label: t('stats.streakLegendary'), color: "from-purple-500 to-pink-500" };
    if (streak >= 14) return { label: t('stats.streakChampion'), color: "from-orange-500 to-red-500" };
    if (streak >= 7) return { label: t('stats.streakOnFire'), color: "from-yellow-500 to-orange-500" };
    if (streak >= 3) return { label: t('stats.streakExcellent'), color: "from-green-800 to-green-900" };
    return { label: t('stats.streakStart'), color: "from-blue-500 to-cyan-500" };
  };

  const getCurrentDateInUserTimezone = (timezone) => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'Europe/Rome',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(now); // Returns YYYY-MM-DD
  };

  const checkIfEvening = useCallback(() => {
    const hour = new Date().getHours();
    setIsEvening(hour >= 18 || hour < 6);
  }, []);

  const isEveningTime = () => {
    const hour = new Date().getHours();
    return hour >= 18;
  };

  const checkTodayCheckin = useCallback(async () => {
    if (!userId) {
      setCheckinLoading(false);
      return;
    }
    setCheckinLoading(true);
    try {
      const res = await axios.get(`${API}/checkin/today/${userId}`);
      setCheckinDone(!!res.data?.checkin_id);
    } catch (e) {
      console.error('[Dashboard] checkTodayCheckin error:', e.response?.data || e);
    } finally {
      setCheckinLoading(false);
    }
  }, [userId, API]);

  /* --------------------------------------------------
     FETCH FUNCTIONS
  -------------------------------------------------- */

  const fetchUpcomingEvents = useCallback(async () => {
    if (!userId || !isPremium) {
      setUpcomingEvents([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/events/upcoming/${userId}?days=15`);
      setUpcomingEvents(res.data || []);
    } catch (e) {
      console.error('[Dashboard] fetchUpcomingEvents error:', e.response?.data || e);
    }
  }, [userId, isPremium, API]);

  const fetchTodaySuggestion = useCallback(
    async (forceLatest = false) => {
      if (!userId) {
        setSuggestionLoading(false);
        return;
      }
      if (!hasPremiumFeatureAccess) {
        setSuggestion(null);
        setSuggestionLoading(false);
        setLoading(false);
        return;
      }
      setSuggestionLoading(true);
      try {
        setLoading(true);
        const url = forceLatest
          ? `${API}/suggestion/history/${userId}?limit=1`
          : `${API}/suggestion/today/${userId}`;

        const res = await axios.get(url);
        if (res.data) {
          const s = Array.isArray(res.data) ? res.data[0] : res.data;
          if (s && !s.status) s.status = 'pending';
          setSuggestion(s || null);
          // KPI: track suggestion shown
          if (s?.suggestion_id) {
            trackSuggestionShown(userId, {
              suggestionId: s.suggestion_id,
              category: s.main_suggestion?.category,
              role: s.main_suggestion?.role,
            });
          }

          // reflect persisted skip / alternative-limit state (persisted by backend or client)
          if (s?.status === 'skipped') {
            setIsSkipped(true);
            setLimitReached(true);
          } else {
            setIsSkipped(false);
            setLimitReached(false);
          }
        } else {
          // No suggestion for today, check if we already generated one today
          const today = getCurrentDateInUserTimezone(user?.timezone);
          if (user?.last_suggestion_date === today) {
            // Already generated today, don't generate again
            console.log('[Dashboard] Suggestion already generated today, not generating new one');
          } else {
            // Generate a new one automatically
            setNeedsGenerate(true);
          }
        }
      } catch (e) {
        console.error('[Dashboard] fetchTodaySuggestion error:', e.response?.data || e);
        if (e.response?.status !== 404) {
          toast.error(i18n.language === 'it' ? 'Errore nel caricamento del suggerimento' : i18n.language === 'es' ? 'Error al cargar la sugerencia' : 'Error loading suggestion');
        }
      } finally {
        setLoading(false);
        setSuggestionLoading(false);
      }
    },
    [userId, i18n.language, API, user?.last_suggestion_date, user?.timezone, hasPremiumFeatureAccess]
  );

  async function checkAndGenerateSuggestion() {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/suggestion/check-and-generate/${userId}`);
      if (res.data.generated) {
        const newSuggestion = { ...res.data.suggestion, status: 'pending' };
        setSuggestion(newSuggestion);
      } else if (res.data.suggestion) {
        const s = res.data.suggestion;
        if (!s.status || s.status === 'pending') s.status = 'pending';
        setSuggestion(s);
      }
    } catch (e) {
      console.error('[Dashboard] checkAndGenerateSuggestion error:', e.response?.data || e);
    }
  }

  async function requestNewSuggestion() {
    if (!userId || !isPremium) return;
    try {
      setRequestingNew(true);
      const res = await axios.post(`${API}/suggestion/generate-new/${userId}`);
      const newSuggestion = { ...res.data, status: 'pending' };
      setSuggestion(newSuggestion);
      setLimitReached(false);
      if (res.data.remaining_requests !== undefined) {
        setRemainingRequests(res.data.remaining_requests);
      }
      //toast.success(i18n.language === 'it' ? 'Nuovo suggerimento generato!' : i18n.language === 'es' ? '¡Nueva sugerencia generada!' : 'New suggestion generated!');
    } catch (e) {
      console.error('[Dashboard] requestNewSuggestion error:', e.response?.data || e);
      const errorMsg =
        e.response?.data?.detail ||
        (i18n.language === 'it' ? 'Errore nella generazione' : i18n.language === 'es' ? 'Error en la generación' : 'Generation error');
      toast.error(errorMsg);
    } finally {
      setRequestingNew(false);
    }
  }

  /* --------------------------------------------------
     FETCH ALL
  -------------------------------------------------- */

  const fetchAll = useCallback(() => {
    if (userLoading || !userId || dataFetched) return;

    // Language sync is handled by UserContext — respect localStorage preference
    const savedLang = localStorage.getItem('betterhusbandUserLang');
    const lang = savedLang || user?.language;
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    setDataFetched(true);
    fetchTodaySuggestion();
    fetchUpcomingEvents();
    checkIfEvening();
    checkTodayCheckin();
    // Optionally:
    // checkAndGenerateSuggestion();
  }, [
    userLoading,
    userId,
    dataFetched,
    user,
    i18n,
    fetchTodaySuggestion,
    fetchUpcomingEvents,
    checkIfEvening,
    checkTodayCheckin
  ]);

  /* --------------------------------------------------
     HELPERS (defined BEFORE effects that reference them)
  -------------------------------------------------- */

  const handleNotificationClick = useCallback(
    async (action) => {
      switch (action) {
        case 'suggestion':
          const el = document.getElementById('main-scroll-container');
          if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
          else window.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        case 'checkin':
          setShowCheckin(true);
          break;
        case 'events':
          navigate('/events');
          break;
        case 'subscription':
          navigate('/subscription');
          break;
        case 'achievements':
          navigate('/achievements');
          break;
        case 'progressive':
          if (!hasPremiumFeatureAccess) {
            redirectToSubscription();
            break;
          }
          try {
            const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            if (!progressiveQuestion) {
              const res = await axios.get(`${API}/profile/progressive/next`, cfg);
              if (res.data && res.data.key) setProgressiveQuestion(res.data);
            }
          } catch (e) {
            console.error('Error loading progressive question:', e);
          }
          setShowProgressiveModal(true);
          break;
        default:
          break;
      }
    },
    [navigate, progressiveQuestion, token, API, hasPremiumFeatureAccess, redirectToSubscription]
  );

  const generateNewSuggestion = useCallback(async () => {
    if (!userId) return;
    if (!hasPremiumFeatureAccess) {
      setSuggestion(null);
      setLimitReached(false);
      return;
    }

    try {
      setLoading(true);

      // Premium: AI-generated
      const res = await axios.post(`${API}/suggestion/generate-new/${userId}`);
      setSuggestion({ ...res.data, status: 'pending' });
      setLimitReached(false);

      fetchUpcomingEvents();

      // Update last_suggestion_date
      const today = getCurrentDateInUserTimezone(user?.timezone);
      try {
        await axios.put(`${API}/user/${userId}`, { last_suggestion_date: today });
        // Update local user state
        if (fetchUserData) {
          await fetchUserData();
        }
      } catch (updateError) {
        console.error('[Dashboard] Error updating last_suggestion_date:', updateError);
      }
    } catch (e) {
      console.error('[Dashboard] generateNewSuggestion error:', e.response?.data || e);

      if (e.response?.status === 429) {
        const errorMessage =
          e.response?.data?.detail ||
          (i18n.language === 'it' ? 'Limite giornaliero raggiunto' : i18n.language === 'es' ? 'Límite diario alcanzado' : 'Daily limit reached');

        toast.error(errorMessage, { duration: 5000 });

        // Paywall only for free users
        if (errorMessage.includes('gratuito') || errorMessage.includes('free')) {
          redirectToSubscription();
        }
      } else {
        toast.error(
          i18n.language === 'it' ? 'Errore nella generazione del suggerimento' : i18n.language === 'es' ? 'Error al generar la sugerencia' : 'Error generating suggestion'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [userId, i18n.language, fetchUpcomingEvents, API, fetchUserData, user?.timezone, hasPremiumFeatureAccess, redirectToSubscription]);

  /* --------------------------------------------------
     SET HEADER PROPS (safe now)
  -------------------------------------------------- */

  useEffect(() => {
    const today = new Date();
    const dateString = today.toLocaleDateString(i18n.language === 'it' ? 'it-IT' : i18n.language === 'es' ? 'es-ES' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    setHeaderProps({
      title: i18n.language === 'it' ? 'Oggi' : i18n.language === 'es' ? 'Hoy' : 'Today',
      user,
      userId,
      onNotificationClick: handleNotificationClick,
      notificationIconClass: 'w-8 h-8', // per la campanella
      notificationIconInnerClass: 'w-4 h-4',
      avatarClass: 'w-8 h-8', // per l'avatar
      rightElement: isAdmin ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => { setShowCheckin(true); }}
            style={{ background: 'var(--c-btn-icon-bg)', color: 'var(--c-btn-icon-fg)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            title="Evening Check-in"
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>🌙</span>
          </button>
          <button
            onClick={async () => {
              try {
                const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
                const res = await axios.get(`${API}/profile/progressive/next`, cfg);
                if (res.data && res.data.key) {
                  setProgressiveQuestion(res.data);
                  setShowProgressiveModal(true);
                }
              } catch (e) { console.error('Progressive fetch failed:', e); }
            }}
            style={{ background: 'var(--c-btn-icon-bg)', color: 'var(--c-btn-icon-fg)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            title="Progressive Question"
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>❓</span>
          </button>
          <button
            onClick={() => { setGardenGrid(prev => stepGrowth(prev).grid); }}
            style={{ background: 'var(--c-btn-icon-bg)', color: 'var(--c-btn-icon-fg)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            title="Grow"
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>🌱</span>
          </button>
          <button
            onClick={() => { setGardenGrid(createInitialGarden()); }}
            style={{ background: 'var(--c-btn-icon-bg)', color: 'var(--c-btn-icon-fg)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            title="Reset Garden"
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>↺</span>
          </button>
        </div>
      ) : null,
    });
  }, [t, user, userId, loading, setHeaderProps, handleNotificationClick, generateNewSuggestion, i18n.language, isAdmin, setGardenGrid, token, API]);

  /* --------------------------------------------------
     EFFECTS
  -------------------------------------------------- */

  useEffect(() => {
    if (userLoading || !userId) return;

    fetchAll();

    const interval = setInterval(checkIfEvening, 300000);

    const onResume = () => {
      setDataFetched(false);
      fetchAll();
    };

    const resumeHandle = CapacitorApp.addListener('resume', onResume);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') onResume();
    };
    visibilityListenerRef.current = onVisibility;
    window.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      // CapacitorApp.addListener restituisce una Promise su web, un oggetto con remove() su mobile
      if (resumeHandle && typeof resumeHandle.then === 'function') {
        // Su web: Promise, non serve cleanup
      } else if (resumeHandle && typeof resumeHandle.remove === 'function') {
        // Su mobile: oggetto con remove()
        resumeHandle.remove();
      }
      window.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userLoading, userId, fetchAll, checkIfEvening]);

  useEffect(() => {
    if (!userId || !isPremium) return;

    const fetchRemainingRequests = async () => {
      try {
        const res = await axios.get(`${API}/suggestion/remaining-requests/${userId}`);
        if (res.data.remaining_requests !== undefined) {
          setRemainingRequests(res.data.remaining_requests);
        }
      } catch (e) {
        console.error('[Dashboard] remaining-requests error:', e.response?.data || e);
      }
    };

    fetchRemainingRequests();
  }, [userId, isPremium, API]);

  // Auto-generate suggestion if none exists
  useEffect(() => {
    if (needsGenerate && userId && generateNewSuggestion) {
      generateNewSuggestion();
      setNeedsGenerate(false);
    }
  }, [needsGenerate, userId, generateNewSuggestion]);

  /* --------------------------------------------------
     UI HELPERS
  -------------------------------------------------- */

  const getRandomEncouragement = () => {
    const messages = t('dashboard.congratulations', { returnObjects: true });
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const handleFeedback = async (status) => {
    if (!suggestion) {
      console.warn('[Dashboard] handleFeedback: no suggestion present');
      return;
    }

    if (status === 'internalized') {
      setInternalizedHidden(true);
      toast(i18n.language === 'it' ? 'Ricevuto, questa azione non ti verrà più mostrata!' : i18n.language === 'es' ? '¡Entendido, esta acción no se mostrará de nuevo!' : 'Got it, this action won\'t be shown again!', {
        style: { background: '#ffffff', color: '#000000' },
      });
      try {
        console.log('[Dashboard] internalized: sending action for suggestion', suggestion);
        const resp = await axios.post(`${API}/suggestion/action`, {
          suggestion_id: suggestion.suggestion_id,
          action: 'INTERNALIZED'
        });
        console.log('[Dashboard] internalized: got response', resp?.data);
        // Note: We intentionally keep suggestion status unchanged to keep "Not today" button visible
        // KPI tracking
        trackSuggestionAction(userId, {
          suggestionId: suggestion.suggestion_id,
          action: 'INTERNALIZED',
          category: suggestion.main_suggestion?.category,
        });
        // refresh counters/language etc
        refreshStats();
        if (user && fetchUserData) {
          await fetchUserData();
        }
      } catch (e) {
        console.error('[Dashboard] internalized error:', e.response?.data || e);
        toast.error(i18n.language === 'it' ? 'Errore nel salvataggio del feedback' : i18n.language === 'es' ? 'Error al guardar el feedback' : 'Error saving feedback');
      }
      return;
    }

    if (status === 'done') {
      setIsCompleting(true);
      // Haptic buzz
      try { Haptics.impact({ style: ImpactStyle.Medium }); } catch (_) {}
      // Morph button to check
      setDoneCheckVisible(true);
      await new Promise(r => setTimeout(r, 600));
      try {
        const feedbackPayload = { suggestion_id: suggestion.suggestion_id, status: 'done' };
        if (suggestion.is_manual && suggestion.main_suggestion?.text) {
          feedbackPayload.replaced_text = suggestion.main_suggestion.text;
        }
        await axios.post(`${API}/suggestion/feedback`, feedbackPayload);
        setSuggestion({ ...suggestion, status: 'done' });
        setIsSkipped(false);
        setLimitReached(false);
        refreshStats();
        window.dispatchEvent(new Event('notifications-refresh'));

        // KPI tracking — emit DONE into events_raw via client queue
        trackSuggestionAction(userId, {
          suggestionId: suggestion.suggestion_id,
          action: 'DONE',
          category: suggestion.main_suggestion?.category,
        });

        // Set random completion phrase
        const phrases = t('dashboard.completion.phrases', { returnObjects: true });
        const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];
        setCompletionPhrase(randomPhrase);

        // Reset consecutive skips
        if (user && fetchUserData) {
          await fetchUserData();
        }

        // Garden growth — seed falls first, then plant appears
        const growResult = stepGrowth(gardenGrid);
        if (growResult.changed && growResult.changedTile) {
          setCompletionAnim({ tile: growResult.changedTile, start: performance.now() });
          setTimeout(() => setGardenGrid(growResult.grid), 300);
          // Clear animation state after it finishes so it won't replay on navigation
          setTimeout(() => setCompletionAnim(null), 2000);
        }

        // Forest pulse animation
        setForestPulse(true);
        setTimeout(() => setForestPulse(false), 1200);

        // Request app store review once after the first completed action.
        try {
          const platform = (await import('@capacitor/core')).Capacitor.getPlatform();
          if (platform === 'ios' || platform === 'android') {
            const reviewKey = `bh_review_requested_${userId}`;
            if (!localStorage.getItem(reviewKey)) {
              localStorage.setItem(reviewKey, '1');
              setTimeout(async () => {
                try {
                  const { InAppReview } = await import('@capacitor-community/in-app-review');
                  await InAppReview.requestReview();
                } catch (reviewErr) {
                  console.debug('[Dashboard] In-app review failed:', reviewErr?.message);
                }
              }, 2000);
            }
          }
        } catch (reviewErr) {
          console.debug('[Dashboard] Review check skipped:', reviewErr?.message);
        }

        // Check for newly unlocked achievements
        if (canSeeDetailedStats) {
          try {
            const { supabase } = await import('@/lib/supabase');
            const { data: { session } } = await supabase.auth.getSession();
            const authToken = session?.access_token || token;
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
            const achRes = await axios.get(`${API}/achievements/${userId}`, { headers });
            const achievements = achRes.data?.achievements || [];
            // Find all achievements unlocked in last 30 seconds (just now)
            const thirtySecsAgo = new Date(Date.now() - 30000).toISOString();
            const brandNew = achievements.filter(a => a.is_unlocked && a.unlocked_at && a.unlocked_at >= thirtySecsAgo);
            if (brandNew.length > 0) {
              setTimeout(() => {
                setCelebrationAchievement(brandNew[0]);
                if (brandNew.length > 1) setCelebrationQueue(brandNew.slice(1));
              }, 800);
            }
          } catch (achErr) {
            console.debug('[Dashboard] achievement check failed:', achErr?.message);
          }
        }

      } catch (e) {
        console.error('[Dashboard] done error:', e.response?.data || e);
        toast.error(t('dashboard.completion.error') || 'Errore. Riprova.');
      } finally {
        setIsCompleting(false);
        setTimeout(() => setDoneCheckVisible(false), 400);
      }
      return;
    }

    if (status === 'skip') {
      // first mark suggestion as skipped on the backend
      try {
        await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status: 'skipped' });
        setSuggestion({ ...suggestion, status: 'skipped' });
        setIsSkipped(true);
        refreshStats();
        // KPI tracking — emit SKIP into events_raw via client queue
        trackSuggestionAction(userId, {
          suggestionId: suggestion.suggestion_id,
          action: 'SKIP',
          category: suggestion.main_suggestion?.category,
        });
        // refresh user data so consecutive_skips is up to date for banner logic
        await fetchUserData();
      } catch (e) {
        console.error('[Dashboard] skip error (marking):', e.response?.data || e);
        toast.error(i18n.language === 'it' ? 'Errore nel saltare il suggerimento' : i18n.language === 'es' ? 'Error al omitir la sugerencia' : 'Error skipping suggestion');
        return;
      }

      // after marking, show the appropriate modal instead of auto‑generating
      if (isPremium) {
        setShowAlternativeModal(true);
      } else {
        redirectToSubscription();
      }

      // Check for skip banner counters (identical to previous implementation)
      const newConsecutive = (user?.consecutive_skips || 0) + 1;
      if (newConsecutive === 5 && !user?.skip_banner_shown_5) {
        setSkipBannerText(t('dashboard.skip.banner5') || "Son 5 giorni che passi. Oggi è una buona occasione.");
        setShowSkipBanner(true);
      } else if (newConsecutive === 10 && !user?.skip_banner_shown_10) {
        setSkipBannerText(t('dashboard.skip.banner10') || "Non serve molto. Solo un gesto.");
        setShowSkipBanner(true);
      }

      // rotate skip texts for future taps
      setSkipTextIndex((prevIndex) => (prevIndex + 1) % skipTexts.length);
      return;
    }

    if (status === 'revert') {
      // Revert skip
      try {
        await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status: 'pending', reverted: true });
        setSuggestion({ ...suggestion, status: 'pending' });
        setIsSkipped(false);
        setLimitReached(false);
        refreshStats();
      } catch (e) {
        console.error('[Dashboard] revert error:', e.response?.data || e);
        toast.error(i18n.language === 'it' ? 'Errore nel ripristinare il suggerimento' : i18n.language === 'es' ? 'Error al restaurar la sugerencia' : 'Error reverting suggestion');
      }
      return;
    }

    if (status === 'alternatives') {
      // Show alternative suggestion modal for premium users, paywall for free users
      if (isPremium) {
        setShowAlternativeModal(true);
      } else {
        redirectToSubscription();
      }
    }

    try {
      await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status });
      //toast.success(t('dashboard.feedbackSuccess'));
      setSuggestion({ ...suggestion, status });
      refreshStats();
    } catch (e) {
      console.error('[Dashboard] feedback error:', e.response?.data || e);
      toast.error(i18n.language === 'it' ? 'Errore nel salvataggio del feedback' : i18n.language === 'es' ? 'Error al guardar el feedback' : 'Error saving feedback');
    }
  };

  const handleRegeneratePrecise = async () => {
    if (!suggestion) return;

    setShowPremiumPrecisionModal(false);
    setRequestingNew(true);

    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const res = await axios.post(`${API}/suggestion/alternative/${userId}`, {
        current_suggestion_id: suggestion.suggestion_id
      }, config);

      setSuggestion(res.data);
      setLimitReached(false);
      setIsSkipped(false); // Reset skip state
      toast.success(t('dashboard.alternative.success') || 'Ecco un\'altra idea!');
    } catch (e) {
      console.error('[Dashboard] regenerate precise error:', e.response?.data || e);
      if (e.response?.status === 429) {
        // silently close the day for alternatives
        setLimitReached(true);
        setIsSkipped(true);

        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture('alternative_limit_reached', {
            user_id: userId || null,
            suggestion_id: suggestion?.suggestion_id || null,
            source: 'dashboard_regenerate',
            timestamp: new Date().toISOString()
          });
        }

        setSuggestion(prev => prev ? { ...prev, status: 'skipped' } : prev);
        // persist skip so it survives reloads
        try {
          const persistCfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
          if (suggestion?.suggestion_id) {
            await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status: 'skipped' }, persistCfg);

            if (window.posthog && typeof window.posthog.capture === 'function') {
              window.posthog.capture('skip_marked_from_limit', {
                user_id: userId || null,
                suggestion_id: suggestion?.suggestion_id || null,
                source: 'dashboard_regenerate_persist',
                timestamp: new Date().toISOString()
              });
            }
          }
        } catch (persistErr) {
          console.error('[Dashboard] persist skip after limit error:', persistErr.response?.data || persistErr);
        }
        return;
      }
      toast.error(t('dashboard.alternative.error') || "Errore nel caricamento dell'alternativa");
    } finally {
      setRequestingNew(false);
    }
  };

  const handleSaveWithNotes = async () => {
    if (!suggestion) return;

    try {
      await axios.post(
        `${API}/suggestion/feedback`,
        {
          suggestion_id: suggestion.suggestion_id,
          status: 'done',
          notes: notes.trim() || null
        }
      );

      setSuggestion({ ...suggestion, status: 'done', notes: notes.trim() });
      setShowNoteSheet(false);
      setNotes('');
      refreshStats();
      //toast.success(t('dashboard.completion.noteSaved') || 'Nota salvata');
    } catch (e) {
      console.error('[Dashboard] save notes error:', e.response?.data || e);
      toast.error(t('dashboard.completion.error') || 'Errore. Riprova.');
    }
  };

  const handleGrokAsk = async () => {
    if (!grokQuestion.trim()) return;

    setGrokLoading(true);
    try {
      const response = await axios.post(
        `${API}/grok/ask`,
        {
          question: grokQuestion,
          context: suggestion?.main_suggestion?.text || '',
          suggestion_id: suggestion?.suggestion_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGrokResponse(response.data.response);
    } catch (e) {
      console.error('[Dashboard] grok error:', e.response?.data || e);
      toast.error('Errore nel contattare Grok');
    } finally {
      setGrokLoading(false);
    }
  };

  const handleProgressiveSave = async (key, value) => {
    if (!hasPremiumFeatureAccess) {
      redirectToSubscription();
      return;
    }
    try {
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.post(`${API}/profile/progressive/answer`, { key, value }, cfg);
      setProgressiveQuestion(null);
      window.dispatchEvent(new Event('progressive-answered'));
    } catch (error) {
      console.error("Error saving progressive answer:", error);
    }
  };

  const handleProgressiveSkip = () => {
    setProgressiveQuestion(null);
  };

  const handleAlternativeSuggestion = async () => {
    if (!suggestion) return;
    if (limitReached) {
      setShowAlternativeOption(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/suggestion/alternative`,
        {
          suggestion_id: suggestion.suggestion_id,
          alternative_count: alternativeCount + 1
        }
      );

      setSuggestion(response.data);
      setLimitReached(false);
      setAlternativeCount(prev => prev + 1);
      setShowAlternativeOption(false);
      toast.success(i18n.language === 'it' ? 'Ecco un\'altra idea!' : i18n.language === 'es' ? '¡Aquí tienes otra idea!' : 'Here\'s another idea!', { duration: 3000 });
    } catch (e) {
      console.error('[Dashboard] alternative suggestion error:', e.response?.data || e);
      if (e.response?.status === 429) {
        // mark closed for today (silent)
        setLimitReached(true);
        setIsSkipped(true);
        setSuggestion(prev => prev ? { ...prev, status: 'skipped' } : prev);
        try {
          const persistCfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
          if (suggestion?.suggestion_id) {
            await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status: 'skipped' }, persistCfg);
          }
        } catch (persistErr) {
          console.error('[Dashboard] persist skip after limit error:', persistErr.response?.data || persistErr);
        }
        return;
      }
      toast.error(i18n.language === 'it' ? 'Errore nel caricamento dell\'alternativa' : i18n.language === 'es' ? 'Error al cargar la alternativa' : 'Error loading alternative');
    }
  };

  const handleGetAlternativeSuggestion = () => {
    if (limitReached) {
      setShowAlternativeOption(false);
      return;
    }
    if (isPremium) {
      if (alternativeCount < 5) {
        handleAlternativeSuggestion();
      } else {
        toast.info(i18n.language === 'it' ? 'Hai visto tutte le alternative disponibili oggi' : i18n.language === 'es' ? 'Has visto todas las alternativas disponibles hoy' : 'You\'ve seen all available alternatives today', { duration: 3000 });
      }
    } else {
      redirectToSubscription();
      setShowAlternativeOption(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'husband':
        return <Icon icon="solar:heart-linear" className="w-5 h-5" />;
      case 'father':
        return <Icon icon="solar:baby-linear" className="w-5 h-5" />;
      case 'partner':
        return <Icon icon="solar:user-linear" className="w-5 h-5" />;
      default:
        return <Icon icon="solar:heart-linear" className="w-5 h-5" />;
    }
  };

  const getContextLabel = (context) => {
    const contextMap = {
      morning: i18n.language === 'it' ? (<><span role="img" aria-label="Mattina">🌅</span> Mattina</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Mañana">🌅</span> Mañana</>) : (<><span role="img" aria-label="Morning">🌅</span> Morning</>),
      afternoon: i18n.language === 'it' ? (<><span role="img" aria-label="Pomeriggio">☀️</span> Pomeriggio</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Tarde">☀️</span> Tarde</>) : (<><span role="img" aria-label="Afternoon">☀️</span> Afternoon</>),
      evening: i18n.language === 'it' ? (<><span role="img" aria-label="Sera">🌙</span> Sera</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Noche">🌙</span> Noche</>) : (<><span role="img" aria-label="Evening">🌙</span> Evening</>),
      anytime: i18n.language === 'it' ? (<><Icon icon="solar:clock-circle-linear" className="inline w-4 h-4 mr-1 align-text-bottom" />Quando vuoi</>) : i18n.language === 'es' ? (<><Icon icon="solar:clock-circle-linear" className="inline w-4 h-4 mr-1 align-text-bottom" />Cuando quieras</>) : (<><Icon icon="solar:clock-circle-linear" className="inline w-4 h-4 mr-1 align-text-bottom" />Anytime</>),
      weekend: i18n.language === 'it' ? (<><span role="img" aria-label="Weekend">🛋️</span> Nel weekend</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Weekend">🛋️</span> El fin de semana</>) : (<><span role="img" aria-label="Weekend">🛋️</span> On the weekend</>),
    };
    return contextMap[context] || String(context || '').replace(/_/g, ' ');
  };

  const getTimeIndicator = (context) => {
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
    if (context === 'anytime') return null;
    return context === timeOfDay;
  };

  /* --------------------------------------------------
     RENDER
  -------------------------------------------------- */

  if (loading || userLoading || suggestionLoading) {
    return <DashboardSkeleton />;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--c-texPri)' }}>
            {i18n.language === 'it' ? 'Errore di caricamento' : i18n.language === 'es' ? 'Error de carga' : 'Loading error'}
          </h2>
          <p className="mb-4" style={{ color: 'var(--c-texSec)' }}>
            {i18n.language === 'it'
              ? 'Impossibile caricare i dati utente. Riprova o torna al login.'
              : 'Unable to load user data. Try again or go back to login.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-full font-medium hover:bg-stone-800 transition-colors" style={{ backgroundColor: 'var(--c-texPri)', color: 'var(--c-texDis)' }}
            >
              {i18n.language === 'it' ? 'Riprova' : i18n.language === 'es' ? 'Reintentar' : 'Retry'}
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 border border-stone-200 rounded-full font-medium hover:bg-stone-50 transition-colors" style={{ color: 'var(--c-texPri)' }}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      margin: 0,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif',
      boxSizing: 'border-box'
    }}>
     
      {/* Content Section — restyled phone mock for "Oggi" */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 0' }}>
        <div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', minHeight: 300, background: 'inherit', position: 'relative', paddingBottom: 0 }}>

          {/* local styles for shimmer + small animations */}
          <style>{`
            @keyframes shimmer { to { left: 200%; } }
            @keyframes fadeUp { to { opacity: 1; transform: translateY(0); } }

            /* completion phrase appear animation */
            @keyframes fadeInUpShort {
              from { opacity: 0; transform: translateY(8px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .completion-phrase {
              display: inline-block;
              animation: fadeInUpShort 360ms cubic-bezier(.16,1,.3,1) both;
              color: var(--c-texSec);
            }

            /* ── Check-morph animation ── */
            @keyframes morphToCheck {
              0%   { border-radius: 999px; width: 160px; height: 52px; }
              50%  { border-radius: 50%;   width: 52px;  height: 52px; }
              100% { border-radius: 50%;   width: 52px;  height: 52px; }
            }
            @keyframes drawCheck {
              to { stroke-dashoffset: 0; }
            }
            @keyframes checkBounce {
              0%   { transform: scale(0.7); opacity: 0; }
              60%  { transform: scale(1.15); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
            .done-morphing {
              animation: morphToCheck 400ms cubic-bezier(.4,0,.2,1) forwards;
              background: #18181b;
              display: flex; align-items: center; justify-content: center;
              overflow: hidden;
            }
            .done-morphing svg { animation: checkBounce 350ms 250ms cubic-bezier(.34,1.56,.64,1) both; }
            .done-morphing svg path {
              stroke-dasharray: 30;
              stroke-dashoffset: 30;
              animation: drawCheck 350ms 300ms ease forwards;
            }

            /* ── Forest pulse ── */
            @keyframes forestGrowPulse {
              0%   { transform: scale(1); filter: brightness(1); }
              40%  { transform: scale(1.06); filter: brightness(1.15); }
              100% { transform: scale(1); filter: brightness(1); }
            }
            .forest-pulse { animation: forestGrowPulse 1.2s cubic-bezier(.34,1.56,.64,1); }
          `}</style>


          {/* Content */}
          <div style={{ padding: 'clamp(12px, 2vw, 20px) clamp(24px, 4vw, 36px) 0' }}>


            {/* Compact action card */}
            <div style={{
              background: 'var(--c-surface-card)',
              border: '1px solid var(--c-border)',
              borderRadius: 16,
              padding: 'clamp(16px, 2.5vw, 24px) clamp(18px, 3vw, 28px)',
              opacity: suggestion?.status === 'done' ? 0.45 : 1,
              transition: 'opacity 0.3s',
            }}>
              {/* Suggestion text */}
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 'clamp(14px, 2vw, 17px)', fontWeight: 500,
                lineHeight: 1.4, color: 'var(--c-texPri)',
                textDecoration: suggestion?.status === 'done' ? 'line-through' : 'none',
              }}>
                {!hasPremiumFeatureAccess
                  ? getTranslation('dashboard.premiumLocked.noSuggestions', 'No suggestions')
                  : (suggestion?.main_suggestion?.text || suggestion?.text || (i18n.language === 'it' ? 'Nessun suggerimento per oggi.' : i18n.language === 'es' ? 'No hay sugerencia para hoy.' : 'No suggestion for today.'))}
              </div>

              {!hasPremiumFeatureAccess && (
                <div style={{ marginTop: 14 }}>
                  <button
                    onClick={redirectToSubscription}
                    style={{
                      width: '100%',
                      height: 44,
                      borderRadius: 999,
                      border: 'none',
                      background: 'var(--c-texPri)',
                      color: 'var(--c-texDis)',
                      fontFamily: 'Manrope-Variable',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {getTranslation('dashboard.premiumLocked.cta', 'Subscribe to unlock your plan')}
                  </button>
                </div>
              )}

              {/* Action row */}
              {suggestion?.main_suggestion && suggestion?.status !== 'done' && (
                <div style={{ marginTop: 14 }}>
                  {!isSkipped && !limitReached && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      {!internalizedHidden && (isPremium || isBetatester || isAdmin) && (
                        <button
                          onClick={() => handleFeedback('internalized')}
                          style={{
                            fontFamily: 'Manrope-Variable', color: 'var(--c-texSec)', background: 'transparent',
                            border: 'none', fontSize: 'clamp(11px, 1.5vw, 13px)', opacity: 0.7, fontWeight: 400, padding: 0, cursor: 'pointer',
                          }}
                        >
                          {i18n.language === 'it' ? 'È già parte di me' : i18n.language === 'es' ? 'Ya es parte de mí' : 'Already part of me'}
                        </button>
                      )}
                      <button
                        onClick={() => { setReplaceActionText(''); setShowReplaceModal(true); }}
                        style={{
                          fontFamily: 'Manrope-Variable', color: 'var(--c-texSec)', background: 'transparent',
                          border: 'none', fontSize: 'clamp(11px, 1.5vw, 13px)', opacity: 0.7, fontWeight: 400, padding: 0, cursor: 'pointer',
                          marginLeft: 'auto',
                        }}
                      >
                        {i18n.language === 'it' ? 'Sostituisci' : i18n.language === 'es' ? 'Sustituir' : 'Replace'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ═══ Hold-to-Complete Button ═══ */}
            {suggestion?.main_suggestion && suggestion?.status !== 'done' && (
              <HoldToCompleteButton
                onComplete={() => handleFeedback('done')}
                label={t('dashboard.done') || (i18n.language === 'it' ? 'Fatto' : i18n.language === 'es' ? 'Hecho' : 'Done')}
                isCompleting={isCompleting}
              />
            )}

            {/* Completion message — below the card */}
            {hasPremiumFeatureAccess && (!suggestion || suggestion?.status === 'done') && (
              <div style={{ textAlign: 'center', padding: '20px 12px 0' }}>
                <div style={{ fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 500, color: 'var(--c-texSec)', letterSpacing: '0.01em', lineHeight: 1.5 }}>
                  {(suggestion?.status === 'done' && completionPhrase) ? (
                    <div className="completion-phrase" data-testid="completion-phrase">{completionPhrase}</div>
                  ) : (
                    i18n.language === 'it' ? "Nient'altro per oggi." : i18n.language === 'es' ? 'Nada más por hoy.' : 'Nothing else for today.'
                  )}
                </div>
              </div>
            )}

          </div>

        </div>
      </div>

      <CheckInModal
        isOpen={showCheckin}
        onClose={() => {
          setShowCheckin(false);
          checkTodayCheckin();
          window.dispatchEvent(new Event('notifications-refresh'));
        }}
        userId={userId}
        todaySuggestion={suggestion}
        isPremium={isPremium}
      />

      <ProgressiveModal
        isOpen={showProgressiveModal}
        onClose={() => setShowProgressiveModal(false)}
        question={progressiveQuestion}
        onSave={async (key, value) => {
          await handleProgressiveSave(key, value);
          setShowProgressiveModal(false);
        }}
        onSkip={() => {
          handleProgressiveSkip();
          setShowProgressiveModal(false);
        }}
      />

      {showPaywall && <PremiumPaywall userId={userId} onClose={() => setShowPaywall(false)} />}

      <ActionDetailPanel
        isOpen={showActionDetail}
        onClose={() => setShowActionDetail(false)}
        suggestion={suggestion}
      />

      {showNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#18181C] rounded-2xl max-w-md w-full p-8 space-y-6 animate-in fade-in zoom-in-95">
            <div>
              <h2 className="font-serif text-2xl text-stone-900 dark:text-[#EFEFEF] mb-2">{t('dashboard.notesTitle') || 'Note'}</h2>
              <p className="text-sm text-stone-500 dark:text-[#606060]">
                {t('dashboard.notesSubtitle') ||
                  (i18n.language === 'it' ? 'Aggiungi note su come è andata' : i18n.language === 'es' ? 'Añade notas sobre cómo fue' : 'Add notes about how it went')}
              </p>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('dashboard.notesPlaceholder') || (i18n.language === 'it' ? 'Scrivi una nota...' : i18n.language === 'es' ? 'Escribe una nota...' : 'Write a note...')}
              className="w-full h-32 p-4 border border-stone-200 dark:border-[#2E2E36] rounded-full focus:border-stone-900 dark:focus:border-[#EFEFEF] focus:ring-0 resize-none bg-white dark:bg-[#222228] text-stone-900 dark:text-[#EFEFEF]"
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowNotes(false);
                  setNotes('');
                }}
                className="flex-1 h-12 rounded-full border border-stone-200 dark:border-[#2E2E36] text-stone-900 dark:text-[#EFEFEF] hover:bg-stone-50 dark:hover:bg-[#222228] transition-all"
              >
                {i18n.language === 'it' ? 'Annulla' : i18n.language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>

              <button
                onClick={handleSaveWithNotes}
                className="flex-1 h-12 rounded-full bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#0E0E10] hover:bg-stone-800 dark:hover:bg-white transition-all"
              >
                {i18n.language === 'it' ? 'Salva' : i18n.language === 'es' ? 'Guardar' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Floating regenerate button — admin only */}
      {isAdmin && (
        <button
          onClick={generateNewSuggestion}
          disabled={loading}
          aria-label={i18n.language === 'it' ? 'Rigenera suggerimento' : i18n.language === 'es' ? 'Regenerar sugerencia' : 'Regenerate suggestion'}
          title={i18n.language === 'it' ? 'Rigenera suggerimento' : i18n.language === 'es' ? 'Regenerar sugerencia' : 'Regenerate suggestion'}
          className="fixed bottom-24 right-4 w-14 h-14 bg-stone-900 text-white rounded-full shadow-lg hover:bg-stone-800 transition-all active:scale-95 active:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center z-40"
        >
          <Icon icon="solar:refresh-linear" className={`w-6 h-6 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}

      {/* Alternative Modal */}
      <AlternativeModal
        isOpen={showAlternativeModal}
        onClose={() => setShowAlternativeModal(false)}
        suggestion={suggestion}
        onAlternativeGenerated={(newSug) => {
          // when the user opts to see an alternative we want to reset skip/limit state
          setSuggestion(newSug);
          setLimitReached(false);
          setIsSkipped(false);
          // alternativeCount is updated inside the modal itself via its prop setter
        }}
        alternativeCount={alternativeCount}
        setAlternativeCount={setAlternativeCount}
      />

      {/* Alternative Paywall for Free Users */}
      <AlternativePaywall
        isOpen={showAlternativePaywall}
        onClose={() => setShowAlternativePaywall(false)}
        onUpgrade={() => {
          setShowAlternativePaywall(false);
          redirectToSubscription();
        }}
      />

      {/* Precision Prompt Modal */}
      {showPrecisionModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/20 p-4">
          <div className="bg-white dark:bg-[#18181C] rounded-t-2xl max-w-md w-full p-6 space-y-4 animate-in slide-in-from-bottom-4 min-h-[32vh]">
            <div className="text-center">
              <h3 className="font-semibold text-lg text-stone-900 dark:text-[#EFEFEF] mb-2">
                {t('dashboard.precision.title') || 'Serve qualcosa di più adatto?'}
              </h3>
              <p className="text-sm text-stone-600 dark:text-[#909090]">
                {t('dashboard.precision.description') || 'Con Precisione il gesto si adatta alla tua giornata.'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPrecisionModal(false)}
                className="flex-1 h-12  border border-stone-200 dark:border-[#2E2E36] text-stone-900 dark:text-[#EFEFEF] hover:bg-stone-50 dark:hover:bg-[#222228] transition-all"
              >
                {t('dashboard.precision.close') || 'Chiudi'}
              </button>
              <button
                onClick={() => {
                  setShowPrecisionModal(false);
                  redirectToSubscription();
                }}
                className="flex-1 h-12  bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#0E0E10] hover:bg-stone-800 dark:hover:bg-white transition-all"
              >
                {t('dashboard.precision.cta') || 'Rendilo più preciso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Precision Modal */}
      {showPremiumPrecisionModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/20 p-4">
          <div className="bg-white dark:bg-[#18181C] rounded-t-2xl max-w-md w-full p-6 space-y-4 animate-in slide-in-from-bottom-4 min-h-[30vh]">
            <div className="text-center">
              <h3 className="font-semibold text-lg text-stone-900 dark:text-[#EFEFEF] mb-2">
                {t('dashboard.premiumPrecision.title') || 'Vuoi un gesto più adatto?'}
              </h3>
              <p className="text-sm text-stone-600 dark:text-[#909090]">{t('dashboard.premiumPrecision.description') || 'Premium adatta il gesto a energia, stress e costanza.'}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPremiumPrecisionModal(false)}
                className="flex-1 h-12 border border-stone-200 dark:border-[#2E2E36] text-stone-900 dark:text-[#EFEFEF] hover:bg-stone-50 dark:hover:bg-[#222228] transition-all"
              >
                {t('dashboard.premiumPrecision.close') || 'No, grazie'}
              </button>
              <button
                onClick={handleRegeneratePrecise}
                className="flex-1 h-12  bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#0E0E10] hover:bg-stone-800 dark:hover:bg-white transition-all"
              >
                {t('dashboard.premiumPrecision.cta') || 'Genera gesto più preciso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Sheet */}
      {showNoteSheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/20 p-4">
          <div className="bg-white dark:bg-[#18181C] rounded-t-2xl max-w-md w-full p-6 space-y-4 animate-in slide-in-from-bottom-4 min-h-[60vh]">
            <div>
              <h3 className="font-semibold text-lg text-stone-900 dark:text-[#EFEFEF] mb-2">
                {t('dashboard.completion.noteTitle') || 'Nota'}
              </h3>
              <p className="text-sm text-stone-600 dark:text-[#909090]">
                {t('dashboard.completion.noteSubtitle') || 'Una riga, se vuoi.'}
              </p>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('dashboard.completion.notePlaceholder') || 'Scrivi una nota...'}
              className="w-full h-24 p-4 border border-stone-200 dark:border-[#2E2E36] rounded-full focus:border-stone-900 dark:focus:border-[#EFEFEF] focus:ring-0 resize-none bg-white dark:bg-[#222228] text-stone-900 dark:text-[#EFEFEF]"
              maxLength={140}
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowNoteSheet(false);
                  setNotes('');
                }}
                className="flex-1 h-12 rounded-full border border-stone-200 dark:border-[#2E2E36] text-stone-900 dark:text-[#EFEFEF] hover:bg-stone-50 dark:hover:bg-[#222228] transition-all"
              >
                {t('dashboard.completion.noteClose') || 'Chiudi'}
              </button>
              <button
                onClick={handleSaveWithNotes}
                className="flex-1 h-12 rounded-full bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#0E0E10] hover:bg-stone-800 dark:hover:bg-white transition-all"
              >
                {t('dashboard.completion.noteSave') || 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grok Modal */}
      {showGrokModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/20 p-4">
          <div className="bg-white dark:bg-[#18181C] rounded-t-2xl max-w-md w-full p-6 space-y-4 animate-in slide-in-from-bottom-4 min-h-[60vh]">
            <div>
              <h3 className="font-semibold text-lg text-stone-900 dark:text-[#EFEFEF] mb-2">
                {t('dashboard.grok.title') || 'Ask Grok'}
              </h3>
              <p className="text-sm text-stone-600 dark:text-[#909090]">
                {t('dashboard.grok.description') || 'Get advice from Grok AI about your gesture or relationship.'}
              </p>
            </div>

            <textarea
              value={grokQuestion}
              onChange={(e) => setGrokQuestion(e.target.value)}
              placeholder={t('dashboard.grok.placeholder') || 'Ask a question...'}
              className="w-full h-24 p-4 border border-stone-200 dark:border-[#2E2E36] rounded-full focus:border-stone-900 dark:focus:border-[#EFEFEF] focus:ring-0 resize-none bg-white dark:bg-[#222228] text-stone-900 dark:text-[#EFEFEF]"
              maxLength={500}
            />

            {grokResponse && (
              <div className="p-4 bg-stone-50 dark:bg-[#222228] rounded-full">
                <p className="text-sm text-stone-700 dark:text-[#EFEFEF]">{grokResponse}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowGrokModal(false);
                  setGrokQuestion('');
                  setGrokResponse('');
                }}
                className="flex-1 h-12 rounded-full border border-stone-200 dark:border-[#2E2E36] text-stone-900 dark:text-[#EFEFEF] hover:bg-stone-50 dark:hover:bg-[#222228] transition-all"
              >
                {t('dashboard.grok.close') || 'Close'}
              </button>
              <button
                onClick={handleGrokAsk}
                disabled={grokLoading || !grokQuestion.trim()}
                className="flex-1 h-12 rounded-full bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#0E0E10] hover:bg-stone-800 dark:hover:bg-white transition-all disabled:opacity-50"
              >
                {grokLoading ? 'Asking...' : (t('dashboard.grok.ask') || 'Ask Grok')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Floating button for free-debug users */}
      {/* Replace Action Modal */}
      {showReplaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
          <div className="bg-white dark:bg-[#18181C] rounded-2xl max-w-md w-full p-6 space-y-4 animate-in slide-in-from-bottom-4">
            <div className="text-center">
              <h3 className="font-semibold text-lg text-stone-900 dark:text-[#EFEFEF] mb-2">
                {i18n.language === 'it' ? 'Cosa hai fatto oggi?' : i18n.language === 'es' ? '¿Qué hiciste hoy?' : 'What did you do today?'}
              </h3>
              <p className="text-sm text-stone-600 dark:text-[#909090]">
                {i18n.language === 'it' ? "Scrivi l'azione che hai già fatto." : i18n.language === 'es' ? 'Escribe la acción que ya hiciste.' : 'Write the action you already did.'}
              </p>
            </div>
            <input
              type="text"
              value={replaceActionText}
              onChange={(e) => setReplaceActionText(e.target.value)}
              placeholder={i18n.language === 'it' ? 'Es. Le ho portato un caffè' : i18n.language === 'es' ? 'Ej. Le llevé un café' : 'E.g. I brought her coffee'}
              maxLength={200}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                border: '1px solid var(--c-border)', fontSize: 14,
                fontFamily: 'Manrope-Variable', outline: 'none',
                background: 'var(--c-surface-card)', color: 'var(--c-texPri)',
              }}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowReplaceModal(false)}
                className="flex-1 h-12 rounded-full border border-stone-200 dark:border-[#2E2E36] text-stone-900 dark:text-[#EFEFEF] hover:bg-stone-50 dark:hover:bg-[#222228] transition-all"
              >
                {i18n.language === 'it' ? 'Annulla' : i18n.language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={() => {
                  if (!replaceActionText.trim()) return;
                  setSuggestion(prev => ({
                    ...prev,
                    main_suggestion: {
                      ...(prev?.main_suggestion || {}),
                      text: replaceActionText.trim(),
                    },
                    is_manual: true,
                  }));
                  setShowReplaceModal(false);
                  setIsSkipped(false);
                  setLimitReached(false);
                }}
                disabled={!replaceActionText.trim()}
                className="flex-1 h-12 rounded-full bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#0E0E10] hover:bg-stone-800 dark:hover:bg-white transition-all disabled:opacity-50"
              >
                {i18n.language === 'it' ? 'Sostituisci' : i18n.language === 'es' ? 'Sustituir' : 'Replace'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isFreeDebug && (
        <button
          onClick={generateNewSuggestion}
          style={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1000,
            background: '#2563eb',
            color: 'white',
            borderRadius: '50%',
            width: 64,
            height: 64,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            fontSize: 32,
            display: 'flex',
            alignItems: 'center',
            border: '2px solid yellow' // highlight for debugging
          }}
        >
          +
        </button>
      )}

      {/* Debug: simulate achievement */}
      {(isAdmin || isFreeDebug) && (
        <button
          onClick={() => setCelebrationAchievement({
            key: 'streak_7', group: 'streak',
            title_en: 'Week Warrior', title_it: 'Guerriero della Settimana',
            desc_en: 'Reach a 7-day streak', desc_it: 'Raggiungi una serie di 7 giorni',
            icon: 'solar:fire-linear', threshold: 7, current: 7,
          })}
          style={{
            position: 'fixed', bottom: 32, left: 24, zIndex: 1000,
            background: '#FFB020', color: '#000', borderRadius: 12,
            padding: '8px 12px', fontSize: 11, fontWeight: 700,
            fontFamily: 'Manrope-Variable', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          🏆 Test
        </button>
      )}

      <AchievementCelebrationModal
        achievement={celebrationAchievement}
        onClose={() => {
          if (celebrationQueue.length > 0) {
            const [next, ...rest] = celebrationQueue;
            setCelebrationAchievement(null);
            setTimeout(() => {
              setCelebrationAchievement(next);
              setCelebrationQueue(rest);
            }, 400);
          } else {
            setCelebrationAchievement(null);
          }
        }}
      />
    </div>
  );
};

export default Dashboard;
